import {
  Alert,
  Box,
  IconButton,
  Snackbar,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DictactionContext } from "../../contexts/DictationProvider";
import {
  UNSAVED_PAGE_WARNING_DEFAULT_MESSAGE,
  UNSAVED_PAGE_WARNING_DEFAULT_TITLE,
  useUnsavedPageWarning,
} from "../../contexts/UnsavedPageWarningContext";
import { Mic, MicOff } from "@mui/icons-material";
import { useDeepgramRecognition } from "../../hooks/useDeepgramRecognition";
import { getRandomInt } from "../../pages/health/encounters/utils";
import { getEncounterDisabledFieldTokens } from "../../theme/encounterDisabledFieldTokens";

type DictationMode = "toggle" | "push-to-talk";

type DictationProps = TextFieldProps & {
  onTextChange: (text: string) => void;
  deepgramApiKey?: string;
  mode?: DictationMode;
};

function DictationControl(userProps: DictationProps) {
  const theme = useTheme();
  const { disabledOutlineStrong } = getEncounterDisabledFieldTokens(theme);

  const mode = userProps.mode || "toggle";

  const deepgramApiKey =
    userProps.deepgramApiKey || import.meta.env.VITE_DEEPGRAM_API_KEY || "";

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollTimeoutRef = useRef<number | null>(null);

  const propsRef = useRef({
    value: userProps.value,
    name: userProps.name,
    onTextChange: userProps.onTextChange,
  });
  useEffect(() => {
    propsRef.current = {
      value: userProps.value,
      name: userProps.name,
      onTextChange: userProps.onTextChange,
    };
  });

  const { addUnSavePageWarning } = useUnsavedPageWarning();
  const prevWasRecordingRef = useRef(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const deepgram = useDeepgramRecognition({
    apiKey: deepgramApiKey,
    onError: (error: string) => {
      setErrorMessage(error);
      setShowError(true);
      setIsActive(false);

      const currentTranscript = deepgram.transcript;
      if (currentTranscript && userProps.value !== currentTranscript) {
        const currentText = userProps.value
          ? `${userProps.value} ${currentTranscript}`
          : currentTranscript;
        userProps?.onTextChange(currentText);
      }
    },
  });

  const { transcript, finalTranscript, listening, resetTranscript } = deepgram;

  const [key] = useState(userProps.key || `${getRandomInt()}`);
  const [fieldDict] = useState(userProps.name);
  const [isActive, setIsActive] = useState(false);

  const { activeKey, setActiveKey, registerStopCallback } =
    useContext(DictactionContext);
  const unregisterStopRef = useRef<(() => void) | null>(null);

  const displayValue = useMemo(() => {
    if (!isActive) return userProps?.value || "";

    const currentValue = userProps.value || "";
    return currentValue ? `${currentValue} ${transcript}` : transcript;
  }, [isActive, userProps.value, transcript]);

  const startListening = useCallback(
    (e) => {
      if (isActive || listening) {
        return;
      }

      if (!isOnline) {
        setErrorMessage(
          "No internet connection. Cloud transcription requires an active network connection.",
        );
        setShowError(true);
        return;
      }

      resetTranscript();
      setActiveKey(key as string);
      unregisterStopRef.current?.();
      unregisterStopRef.current =
        registerStopCallback?.(() => stopListeningRef.current?.()) ?? null;
      setIsActive(true);

      deepgram.startListening({ continuous: true }).catch(() => {
        setIsActive(false);
      });
    },
    [
      isOnline,
      isActive,
      listening,
      resetTranscript,
      setActiveKey,
      key,
      deepgram,
      registerStopCallback,
    ],
  );

  const stopListening = useCallback(async () => {
    const baseValue = (propsRef.current.value as string) || "";

    const accumulated = await deepgram.stopListening();

    if (accumulated && fieldDict === propsRef.current.name) {
      const committed = baseValue
        ? `${baseValue} ${accumulated}`.trim()
        : accumulated;
      propsRef.current.onTextChange(committed);
    }

    setIsActive(false);
    setActiveKey(undefined);
    unregisterStopRef.current?.();
    unregisterStopRef.current = null;
  }, [deepgram, fieldDict, setActiveKey]);

  useEffect(() => {
    if (deepgram.error && isActive) {
      setIsActive(false);
    }
  }, [deepgram.error, isActive]);

  useEffect(() => {
    if (activeKey !== key && (isActive || listening)) {
      stopListening();
    }
  }, [activeKey, key, isActive, listening, stopListening]);

  const toggleListening = useCallback(
    (e) => {
      if (isActive || listening) {
        stopListening();
      } else {
        startListening(e);
      }
    },
    [isActive, listening, stopListening, startListening],
  );

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (fieldDict === userProps.name) {
        userProps?.onTextChange(event.target.value);
      }
    },
    [fieldDict, userProps.name, userProps.onTextChange],
  );

  const startListeningRef = useRef(startListening);
  const stopListeningRef = useRef(stopListening);

  useEffect(() => {
    startListeningRef.current = startListening;
    stopListeningRef.current = stopListening;
  }, [startListening, stopListening]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.ctrlKey && event.target.id) {
        if (event.code === "Space") startListeningRef.current(event);
      }
    };

    const onKeyUp = (event) => {
      if (event.ctrlKey && event.code === "Space") {
        stopListeningRef.current();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  useEffect(() => {
    if (isActive && inputRef.current) {
      if (scrollTimeoutRef.current) {
        cancelAnimationFrame(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
      });
    }

    return () => {
      if (scrollTimeoutRef.current) {
        cancelAnimationFrame(scrollTimeoutRef.current);
      }
    };
  }, [transcript, isActive]);

  useEffect(() => {
    const recordingKey = `dictation-${userProps.name || key}`;
    const isRecording = isActive || listening;

    if (isRecording) {
      addUnSavePageWarning({
        message:
          "You are currently recording audio. Are you sure you want to leave?",
        title: "Recording in progress",
        isUnsaved: [recordingKey, true],
        disableSave: true,
      });
    } else {
      addUnSavePageWarning({
        isUnsaved: [recordingKey, false],
        disableSave: false,
      });
      if (prevWasRecordingRef.current) {
        addUnSavePageWarning({
          message: UNSAVED_PAGE_WARNING_DEFAULT_MESSAGE,
          title: UNSAVED_PAGE_WARNING_DEFAULT_TITLE,
        });
      }
    }
    prevWasRecordingRef.current = isRecording;
  }, [isActive, listening, userProps.name, key, addUnSavePageWarning]);

  return (
    <>
      <TextField
        {...userProps}
        inputRef={inputRef}
        value={displayValue}
        multiline
        onChange={handleTextChange}
        label={userProps.label}
        required={userProps.required}
        color={isActive ? "error" : userProps.color}
        focused={isActive || userProps.focused}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: !userProps.disabled && (
            <InputAdornment position="end">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {isActive && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ fontWeight: 500 }}
                    aria-live="polite"
                  >
                    Listening…
                  </Typography>
                )}
                <Tooltip
                  title={
                    mode === "toggle"
                      ? isActive
                        ? "Click to stop recording"
                        : "Click to start recording"
                      : isActive
                        ? "Release to stop recording"
                        : "Hold to record (Ctrl+Space when focused)"
                  }
                >
                  <IconButton
                    tabIndex={-1}
                    aria-hidden="true"
                    aria-label={
                      mode === "toggle"
                        ? isActive
                          ? "Click to stop recording"
                          : "Click to start recording"
                        : isActive
                          ? "Release to stop recording"
                          : "Hold to record"
                    }
                    name={userProps?.name}
                    onClick={mode === "toggle" ? toggleListening : undefined}
                    onTouchStart={
                      mode === "push-to-talk"
                        ? (e: React.SyntheticEvent) => startListening(e)
                        : undefined
                    }
                    onMouseDown={
                      mode === "push-to-talk"
                        ? (e) => startListening(e)
                        : undefined
                    }
                    onTouchEnd={
                      mode === "push-to-talk" ? stopListening : undefined
                    }
                    onMouseUp={
                      mode === "push-to-talk" ? stopListening : undefined
                    }
                    edge="end"
                    disabled={userProps.disabled}
                  >
                    {!isActive ? <MicOff /> : <Mic color="error" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </InputAdornment>
          ),
        }}
        sx={{
          ...userProps.sx,
          "& .MuiOutlinedInput-root": {
            ...(isActive && {
              "& fieldset": {
                borderWidth: "3px",
                borderColor: "error.main",
              },
            }),
          },
          "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: disabledOutlineStrong,
          },
        }}
      />

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DictationControl;
